import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import AccountsPage from './pages/AccountsPage';
import TransfersPage from './pages/TransfersPage';
import TransactionHistoryPage from './pages/TransactionHistoryPage';
import NotificationsPage from './pages/NotificationsPage';
import NotFoundPage from './pages/NotFoundPage';
import GlobalStyle from './styles/GlobalStyle';
import { getTheme } from './styles/theme';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

// Внутренний компонент для использования темы
function AppContent() {
  const { isDarkMode } = useTheme();
  const currentTheme = getTheme(isDarkMode);

  // Устанавливаем data-theme атрибут для CSS переменных
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    // Также устанавливаем цвет фона для body
    document.body.style.backgroundColor = currentTheme.colors.background;
  }, [isDarkMode, currentTheme]);

  return (
    <StyledThemeProvider theme={currentTheme}>
      <GlobalStyle />
      <Router>
        <Routes>
          {/* Root redirect to example tenant */}
          <Route path="/" element={<Navigate to="/demo-tenant/" replace />} />
          
          {/* Tenant-scoped routes */}
          <Route path="/:tenantName/*" element={<TenantRoutes />} />
          
          {/* 404 for invalid routes */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: isDarkMode ? '#2d2d2d' : '#363636',
            color: isDarkMode ? '#ffffff' : '#fff',
            border: isDarkMode ? '1px solid #404040' : 'none',
          },
          success: {
            style: {
              background: isDarkMode ? '#1e3a2e' : '#d4edda',
              color: isDarkMode ? '#51cf66' : '#155724',
              border: isDarkMode ? '1px solid #51cf66' : '1px solid #c3e6cb',
            },
          },
          error: {
            style: {
              background: isDarkMode ? '#3a1e1e' : '#f8d7da',
              color: isDarkMode ? '#ff6b6b' : '#721c24',
              border: isDarkMode ? '1px solid #ff6b6b' : '1px solid #f5c6cb',
            },
          },
        }}
      />
    </StyledThemeProvider>
  );
}

// Tenant-scoped sub-routes
function TenantRoutes() {
  return (
    <TenantProvider>
      <AuthProvider>
        <NotificationProvider>
          <Routes>
          {/* Public routes */}
          <Route path="/" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          
          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts"
            element={
              <ProtectedRoute>
                <AccountsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transfers"
            element={
              <ProtectedRoute>
                <TransfersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <TransactionHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Catch-all for tenant-scoped 404s */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </NotificationProvider>
      </AuthProvider>
    </TenantProvider>
  );
}

export default App; 